import { sortObject } from '@takeshape/util';
import { combineReducers } from 'redux';
import addToTakeShapeReducer from './add-to-takeshape/reducer';
import { AGENT_EDITOR_FORM_NAME } from './agent/components/agent-editor/constants';
import { agentFormReducer } from './agent/slice';
import { aiFormReducers } from './ai/slice';
import { NAME as API_EXPLORER_NAME, reducer as apiExplorerReducer } from './api-explorer';
import { NAME as API_KEYS_NAME } from './api-keys/constants';
import apiKeysReducer from './api-keys/reducer';
import { NAME as ASSET_PICKER_NAME } from './asset-picker/constants';
import assetPickerReducer from './asset-picker/reducer';
import { NAME as ASSET_UPLOADER_NAME } from './asset-uploader/constants';
import assetUploaderReducer from './asset-uploader/reducer';
import { NAME as ASSETS_NAME } from './assets/constants';
import assetsReducer from './assets/reducer';
import { NAME as AUTH_NAME } from './auth/constants';
import authFormReducers from './auth/form-reducers';
import authReducer from './auth/reducer';
import { NAME as BILLING_NAME } from './billing/constants';
import billingReducer from './billing/reducer';
import { NAME as BRANCHES_NAME } from './branches/constants';
import { reducer as branchesReducer } from './branches/reducer';
import { NAME as COLLAB_NAME } from './collab/constants';
import collabReducer from './collab/reducer';
import { NAME as CONFIRM_NAME } from './confirm/constants';
import confirmReducer from './confirm/reducer';
import { FORM_NAME as CONTENT_FORM_NAME, NAME as CONTENT_NAME } from './content/constants';
import contentFormReducer from './content/form-reducer';
import contentReducer from './content/reducer';
import { CONTENT_LIST_NAME, contentListReducer } from './content-list';
import { NAME as CONTENT_PREVIEW_NAME, reducer as contentPreviewReducer } from './content-preview';
import { CONTENT_SEARCH_NAME, contentSearchReducer } from './content-search';
import { NAME as EXPORT_NAME } from './export/constants';
import exportReducer from './export/reducer';
import { NAME as FORM_DIALOG_NAME } from './form-dialog/constants';
import formDialogReducer from './form-dialog/reducer';
import { guardrailsFormReducers } from './guardrails/form-reducers';
import { formReducer as createPublishTargetReducer } from './integrations/components/create-publish-target';
import { CREATE_PUBLISH_TARGET_FORM_NAME, NAME as INTEGRATIONS_NAME } from './integrations/constants';
import integrationsReducer from './integrations/reducer';
import { NAME as LIVE_LINKS_NAME, reducer as liveLinksReducer } from './live-links';
import { NAME as LOCALES_NAME, formReducers as localesFormReducers, reducer as localesReducer } from './locales';
import { NAME as MESSAGES_NAME } from './messages/constants';
import messagesReducer from './messages/reducer';
import { MULTI_SELECT_NAME, multiSelectReducer } from './multi-select';
import { INVITE_FORM_NAME, NAME as PROJECT_NAME } from './project/constants';
import inviteUserFormReducer from './project/invite-user-form-reducer';
import projectReducer from './project/reducer';
import router from './router';
import { NAME as SCHEDULE_NAME } from './schedule/constants';
import scheduleReducer from './schedule/reducer';
import { SCHEMA_NAME, reducer as schemaReducer } from './schema';
import { SCHEMA_HISTORY_NAME, historyReducer as schemaHistoryReducer } from './schema/slices/get-schema-version-list';
import { NAME as SCHEMA_FORM_NAME } from './schema-form/constants';
import schemaFormReducerFactory from './schema-form/reducer-factory';
import { NAME as SERVICES_NAME, servicesFormReducers, reducer as servicesReducer } from './services';
import { FORM_NAME as SITES_FORM_NAME, NAME as SITES_NAME } from './sites/constants';
import sitesFormReducer from './sites/form-reducer';
import sitesReducer from './sites/reducer';
import { NAME as SSO_NAME } from './sso/constants';
import ssoReducer from './sso/reducer';
import { LIST_NAME as USER_ROLE_LIST_NAME, NAME as USER_ROLE_NAME } from './user-roles/constants';
import userRoleListReducer from './user-roles/list-reducer';
import userRoleReducer from './user-roles/reducer';
import { LIST_NAME as VERSION_LIST_NAME, NAME as VERSION_NAME } from './versions/constants';
import versionListReducer from './versions/list-reducer';
import versionReducer from './versions/reducer';
import { NAME as WEBHOOKS_NAME } from './webhooks/constants';
import webhooksReducer from './webhooks/reducer';
import { NAME as WORKBENCH_NAME, reducer as workbenchReducer } from './workbench';
import { WORKFLOW_FORM_NAME, WORKFLOW_UPDATE_FORM } from './workflows/constants';
import workflowFormReducer from './workflows/form-reducer';

export default combineReducers(
  sortObject({
    [AUTH_NAME]: authReducer,
    [SCHEMA_NAME]: schemaReducer,
    [SCHEMA_HISTORY_NAME]: schemaHistoryReducer,
    [MESSAGES_NAME]: messagesReducer,
    [CONFIRM_NAME]: confirmReducer,
    [FORM_DIALOG_NAME]: formDialogReducer,
    [PROJECT_NAME]: projectReducer,
    [ASSETS_NAME]: assetsReducer,
    [ASSET_UPLOADER_NAME]: assetUploaderReducer,
    [ASSET_PICKER_NAME]: assetPickerReducer,
    [CONTENT_NAME]: contentReducer,
    [CONTENT_SEARCH_NAME]: contentSearchReducer,
    [SITES_NAME]: sitesReducer,
    [MULTI_SELECT_NAME]: multiSelectReducer,
    [BILLING_NAME]: billingReducer,
    [COLLAB_NAME]: collabReducer,
    [API_KEYS_NAME]: apiKeysReducer,
    [CONTENT_LIST_NAME]: contentListReducer,
    [WEBHOOKS_NAME]: webhooksReducer,
    [INTEGRATIONS_NAME]: integrationsReducer,
    [SCHEMA_FORM_NAME]: schemaFormReducerFactory({
      [CONTENT_FORM_NAME]: contentFormReducer,
      [INVITE_FORM_NAME]: inviteUserFormReducer,
      [SITES_FORM_NAME]: sitesFormReducer,
      [CREATE_PUBLISH_TARGET_FORM_NAME]: createPublishTargetReducer,
      [WORKFLOW_FORM_NAME]: workflowFormReducer,
      [WORKFLOW_UPDATE_FORM]: workflowFormReducer,
      [AGENT_EDITOR_FORM_NAME]: agentFormReducer,
      ...authFormReducers,
      ...localesFormReducers,
      ...servicesFormReducers,
      ...guardrailsFormReducers,
      ...aiFormReducers
    }),
    [LOCALES_NAME]: localesReducer,
    [LIVE_LINKS_NAME]: liveLinksReducer,
    [VERSION_NAME]: versionReducer,
    [VERSION_LIST_NAME]: versionListReducer,
    [USER_ROLE_NAME]: userRoleReducer,
    [USER_ROLE_LIST_NAME]: userRoleListReducer,
    [SCHEDULE_NAME]: scheduleReducer,
    [SSO_NAME]: ssoReducer,
    [CONTENT_PREVIEW_NAME]: contentPreviewReducer,
    addToTakeShape: addToTakeShapeReducer,
    [EXPORT_NAME]: exportReducer,
    [SERVICES_NAME]: servicesReducer,
    [API_EXPLORER_NAME]: apiExplorerReducer,
    [WORKBENCH_NAME]: workbenchReducer,
    [BRANCHES_NAME]: branchesReducer,
    router: router.reducer
  })
);
